// @ts-strict-ignore
import { $generateNodesFromDOM, $generateHtmlFromNodes } from "@lexical/html";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { $getRoot, $isDecoratorNode, $isElementNode, $setSelection } from "lexical";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { l } from "@aviary/locales/i18n";
import { e2eAttributes } from "@shared/e2eAttributes";

import * as styles from "../RichTextEditor.styles";
import { Toolbar } from "../Toolbar";

interface Props {
  initialInput?: string;
  onChange?: (htmlMessage: string) => void;
  toolbarInfo?: ReactNode;
  placeholder?: string;
  readOnly?: boolean;
  hideAutoFocus?: boolean;
  ignoreExternalUpdates?: boolean;
}

const LexicalEditor = ({
  initialInput,
  onChange,
  toolbarInfo,
  placeholder,
  readOnly,
  hideAutoFocus,
  ignoreExternalUpdates,
}: Props) => {
  const { t } = useTranslation();

  const [editor] = useLexicalComposerContext();
  const hasMountedRef = useRef(false);
  const hasUserEditedRef = useRef(false);
  const initialInputRef = useRef(initialInput);

  const formatHTML = (messageString: string) => {
    const doc = document.createElement("div");
    doc.innerHTML = messageString;
    // Find unwrapped text nodes
    const textNodes = Array.from(doc.childNodes).filter(
      node => node.nodeType === 3 && node.textContent.trim().length > 1
    );
    // Wrap the text nodes
    textNodes.forEach(node => {
      const wrap = document.createElement("p");
      node.after(wrap);
      wrap.appendChild(node);
    });

    return doc.innerHTML;
  };

  useEffect(() => {
    return () => {
      hasMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    initialInputRef.current = initialInput;
  });

  useEffect(() => {
    if (ignoreExternalUpdates && hasMountedRef.current) return;
    // Don't overwrite content the user has already typed
    if (ignoreExternalUpdates && hasUserEditedRef.current) return;
    if (initialInput) hasMountedRef.current = true;

    if (initialInput) {
      editor.update(() => {
        const str = formatHTML(initialInput);
        const currentEditorHTML = $generateHtmlFromNodes(editor, null);

        if (str !== currentEditorHTML) {
          const parser = new DOMParser();
          const dom = parser.parseFromString(str || "", "text/html");
          const nodes = $generateNodesFromDOM(editor, dom);
          const root = $getRoot();
          root.clear();
          nodes.forEach(node => {
            if ($isElementNode(node) || $isDecoratorNode(node)) {
              root.append(node);
            }
          });

          if (hideAutoFocus) {
            $setSelection(null);
          } else {
            const lastChild = root.getLastChild();

            if (lastChild && $isElementNode(lastChild)) {
              lastChild.selectEnd();
            } else {
              root.selectEnd();
            }
          }
        }
      });
    }

    if (!hideAutoFocus && !readOnly) {
      editor.focus();
    }
  }, [editor, initialInput, hideAutoFocus, readOnly, ignoreExternalUpdates]);

  const handleChange = useCallback(() => {
    if (onChange) {
      editor.update(() => {
        const generatedHtml = $generateHtmlFromNodes(editor, null);
        if (generatedHtml !== initialInputRef.current) {
          hasUserEditedRef.current = true;
          onChange(generatedHtml);
        }
      });
    }
  }, [editor, onChange]);

  const Placeholder = () => {
    return (
      <div css={styles.placeholder}>
        {placeholder ?? t(l.aviary.richTextEditor.placeHolderText)}
      </div>
    );
  };

  return (
    <div css={[styles.base, !readOnly && styles.wrapper]}>
      <OnChangePlugin onChange={handleChange} />
      <HistoryPlugin />
      <RichTextPlugin
        contentEditable={
          <ContentEditable
            ariaLabel={t(l.aviary.richTextEditor.richTextArea)}
            css={!readOnly && styles.textArea}
            readOnly={readOnly}
            data-e2e={e2eAttributes.lexicalEditorContentEditable}
          />
        }
        placeholder={<Placeholder />}
        ErrorBoundary={LexicalErrorBoundary}
      />
      {!readOnly && <Toolbar toolbarInfo={toolbarInfo} />}
      <LinkPlugin />
      <ListPlugin />
      <TabIndentationPlugin />
    </div>
  );
};

export { LexicalEditor };
